import { ComponentType, MutableRefObject, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from "@headlessui/react";
import { RiCoinsLine, RiMenuFoldLine, RiMenuUnfoldLine } from "@remixicon/react";

import { cx } from "utils";

import { ReactComponent as MenuSvg } from "assets/icons/menu-icon.svg";
import { ReactComponent as RebrandMenuSvg } from "assets/icons/menu-icon-2026.svg";
import ScraperAPILogo from "components/ScraperAPILogo";

import Button from "components/Button";
import ExtLink from "components/ExtLink";
import Badge from "components/Badge";
import { useFeatureSwitch } from "components/FeatureSwitch";
import { FeatureSwitch } from "components/FeatureSwitch/FeatureSwitches";
import { rebrandSvg } from "components/FeatureSwitch/rebrandSvg";
import Icons, { IconName } from "components/Icons";
import UserMenu from "components/UserMenu";
import TaggedText from "components/TaggedText";

import { useContactSales } from "hooks/useContactSales";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useRelativeModalPathsForDataRoutes } from "v2/hooks/useRelativeModalPathsForDataRoutes";
import { useSupportWidget } from "hooks/useSupportWidget";
import { useStatuspageIssues } from "v2/hooks/issues/useIssues";
import useIsMobile from "hooks/useIsMobile";

import { RecentlyViewedPage } from "providers/RecentlyViewed/RecentlyViewedProvider";

import { useUser } from "routes/dataroutes/UserData";

import { path as HomePath } from "v2/pages/home/HomeRoute";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { trackAnalyticsSubpageViewed, trackAnalyticsTabViewed } from "utils/Tracking";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


const MobileMenuIcon = rebrandSvg(MenuSvg, RebrandMenuSvg);

const PANEL_WIDTH = "w-[256px]";
const COLLAPSED_WIDTH = "w-[64px]";
const USER_MENU_WIDTH = "w-[270px]";


interface ISidebarItemProps {
  Icon?: ComponentType<any>;
  iconStyle?: string;
  icon?: JSX.Element;
  iconName?: IconName;
  content?: JSX.Element | string;
  contentPadding?: string;
  collapsed?: boolean;
  disabled?: boolean;
  disableWhenBlocked?: boolean;
  active?: boolean;
  onClick?: () => void;
  featureSwitch?: FeatureSwitch;
  trackedTitle?: string;
  subItem?: boolean;
  isNew?: boolean;
  newUntil?: Date | string;
  badgeText?: string;
}

function SidebarItemContent({
  Icon,
  iconStyle,
  icon,
  content,
  contentPadding = "py-3 pr-4",
  collapsed,
  active,
  isNew = false,
  newUntil,
  badgeText,
}: ISidebarItemProps) {
  // Check if the new badge should be shown
  const shouldShowNewBadge = () => {
    if (!isNew || !newUntil) return false;

    const untilDate = new Date(newUntil);
    const currentDate = new Date();

    // Show badge if current date is before the "until" date
    return currentDate < untilDate;
  };

  const showBadge = shouldShowNewBadge();

  return (
    <>
      <div
        className={cx(
          "pl-6 h-12 flex place-content-center items-center border-l-[3px] dark:border-l-0",
          active ? "border-l-brandPrimary" : "border-l-transparent"
        )}
      >
        {Icon && <Icon className={iconStyle} />}
        {icon}
      </div>
      <div
        className={cx(
          "w-full font-droid text-left overflow-hidden whitespace-nowrap relative",
          contentPadding,
          "*:transition-opacity *:duration-700",
          collapsed ? "*:opacity-0" : "*:opacity-100"
        )}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex-1 min-w-0">
            {typeof content === "string" ? (
              <div className="pl-4 w-full whitespace-nowrap flex flex-row">{content}
                {showBadge && !collapsed && (
                  <div className="flex-shrink-0 ml-2">
                    <Badge text={badgeText} />
                  </div>
                )}
              </div>
            ) : (
              <div className="pl-4">{content}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}


// function SidebarItem({ Icon, iconStyle, icon, content, contentPadding, collapsed }: ISidebarItemProps) {
//   return (
//     <div className="flex flex-row items-center w-full">
//       <SidebarItemContent
//         Icon={ Icon }
//         iconStyle={ iconStyle }
//         icon={ icon }
//         content={ content }
//         contentPadding={ contentPadding }
//         collapsed={ collapsed }
//       />
//     </div>
//   );
// }

function useReallyDisabled(disabled?: boolean, disableWhenBlocked?: boolean) {
  const user = useUser();
  return disabled || (disableWhenBlocked && user?.isBlocked);
}

function SidebarNavLink({ disabled, disableWhenBlocked, to, onClick, addLocationToState, featureSwitch, subItem, matchSubPaths, ...props }: ISidebarItemProps & { to: string, addLocationToState?: boolean, matchSubPaths?: boolean }) {
  const location = useLocation();
  const isFeatureEnabled = useFeatureSwitch(featureSwitch);
  const reallyDisabled = useReallyDisabled(disabled, disableWhenBlocked);

  if (!isFeatureEnabled) {
    return <></>;
  }

  // Helper to determine if link should be active
  // When matchSubPaths is true (e.g., for /crawlers), match all sub-paths like /crawlers/123/jobs
  const getActiveState = (navLinkIsActive: boolean): boolean => {
    if (matchSubPaths) {
      return location.pathname.startsWith(to);
    }
    return navLinkIsActive;
  };

  return (
    <NavLink
      to={ to }
      end={ !matchSubPaths }
      className={ ({ isActive }) => cx(
        "flex flex-row items-center w-full transition-colors duration-50",
        (!reallyDisabled && getActiveState(isActive))
          ? "text-brandPrimary dark:text-white dark:bg-primary-600"
          : cx("hover:bg-slate-50 dark:hover:bg-primary-100 hover:text-gray-700 dark:hover:text-neutral-900", subItem && 'bg-neutral-100')
      )}
      onClick={ onClick }
      state={ { backgroundLocation: addLocationToState ? location : undefined } }
    >
      { ({ isActive }) => (
        <SidebarItemContent
          { ...props }
          active={ getActiveState(isActive) }
        />
      )}
    </NavLink>
  );
}

function SidebarDisclosure({
  featureSwitch,
  collapsed,
}: {
  featureSwitch?: FeatureSwitch;
  collapsed: boolean;
}) {
  const location = useLocation();
  const isFeatureEnabled = useFeatureSwitch(featureSwitch);

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <Disclosure>
      {({ open }) => {
        const isActive = open || location.pathname.startsWith('/analytics');
        return (
          <>
            <DisclosureButton
              className={cx(
                "flex flex-row items-center w-full transition-colors duration-50",
                isActive
                  ? "text-brandPrimary"
                  : "hover:bg-slate-50 dark:hover:bg-primary-100 hover:text-gray-700 dark:hover:text-neutral-900"
              )}
            >
              <SidebarItemContent
                Icon={Icons.Status}
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                content="Analytics"
                active={isActive}
                collapsed={collapsed}
                isNew={true}
                newUntil="2025-10-01"
                badgeText="Beta"
              />
              {open ? (
                <ChevronUpIcon className="h-4 w-4 mr-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 mr-4" />
              )}
            </DisclosureButton>

            <DisclosurePanel className="flex flex-col" style={{ marginTop: 0 }}>
              {!collapsed && (
                <>
                  <SidebarNavLink
                    subItem
                    to="/analytics"
                    content="Overview"
                    collapsed={collapsed}
                    active={open}
                    onClick={() => trackAnalyticsTabViewed()}
                  />
                  <SidebarNavLink
                    subItem
                    to="/analytics/domain-analytics"
                    content="Domain Analytics"
                    collapsed={collapsed}
                    active={open}
                    onClick={() => trackAnalyticsSubpageViewed({section: "domain_analytics"})}
                  />
                  <SidebarNavLink
                    subItem
                    to="/analytics/error-logs"
                    content="Error Logs"
                    collapsed={collapsed}
                    active={open}
                    onClick={() => trackAnalyticsSubpageViewed({section: "error_logs"})}
                  />
                </>
              )}
            </DisclosurePanel>
          </>
        );
      }}
    </Disclosure>
  );
}

function SidebarExtLink({ disabled, disableWhenBlocked, href, onClick, ...props }: ISidebarItemProps & { href: string }) {
  const reallyDisabled = useReallyDisabled(disabled, disableWhenBlocked);

  return (
    <RecentlyViewedPage
      title={props.trackedTitle}
      iconName={props.iconName}
    >
      <ExtLink
        to={href}
        className={cx(
          "flex flex-row items-center w-full",
          !reallyDisabled && "hover:bg-slate-50 dark:hover:bg-primary-100 hover:text-gray-700 dark:hover:text-neutral-900"
        )}
        onClick={onClick}
        tracked
        iconName={props.iconName}
      >
        <SidebarItemContent {...props} />
      </ExtLink>
    </RecentlyViewedPage>
  )
}

function SidebarAction({ disabled, disableWhenBlocked, onClick, ...props }: ISidebarItemProps) {
  const reallyDisabled = useReallyDisabled(disabled, disableWhenBlocked);

  return (
    <div
      className={ cx(
        "flex flex-row items-center w-full cursor-pointer",
        !reallyDisabled && "hover:bg-slate-50 dark:hover:bg-primary-100 hover:text-gray-700"  // TODO do not change text color in new design (dark mode)
      ) }
      onClick={ onClick }
    >
      <SidebarItemContent { ...props } />
    </div>
  );
}

const UpgradeButton = ({ onClickCallback }: { onClickCallback?: () => void }) => {

  const { activeSubscription } = useActiveSubscription();
  const bookSalesCallPath = useRelativeModalPathsForDataRoutes("/book-sales-call");

  if (activeSubscription?.planSlug !== "free") {
    return <></>;
  }

  return (
    <div className="flex flex-col items-center justify-center p-4 space-y-4 bg-brandLightest dark:bg-primary-50">
      <p className="text-sm text-brandDarkest dark:text-primary-800 text-center max-w-[220px] mb-2">
        <TaggedText message="Need more time to test and maximize your experience? [Book a call|book_sales_call] and get an extended trial!" />
      </p>
      <Button
        text="Book a call"
        className="button button-primary"
        size="MD"
        href={ bookSalesCallPath }
        shouldPassBackgroundLocation
        onClick={ onClickCallback }
        focusable={ false }
      />
    </div>
  );
};

function StatusBadge({ xOffset, yOffset, className }: { xOffset: string, yOffset: string, className?: string }) {
  const { issues } = useStatuspageIssues();

  return (
    <Transition
      show={ ((issues?.numActiveIssues || 0) > 0) }
    >
      <div className={ cx(
        "flex items-center justify-center w-4 h-4 text-xs text-white bg-brandPrimary-500 dark:bg-warning-600 rounded-full absolute",
        "transition ease-in-out duration-100 data-[closed]:opacity-0 data-[closed]:scale-50",
        xOffset,
        yOffset,
        className
      ) }>
        { issues?.numActiveIssues }
      </div>
    </Transition>
  );
}

interface ILogoAndIconsProps {
  mobile?: boolean;
  toggleMobileMenu?: () => void;
  collapsed?: boolean;
  toggleCollapsed?: () => void;
  showCollapseIcon?: boolean;
}

function LogoAndIcons({ mobile, toggleMobileMenu, collapsed, toggleCollapsed, showCollapseIcon = true }: ILogoAndIconsProps) {
  if (mobile) {
    return (
      <div className="flex items-center justify-between">
        <ScraperAPILogo/>
        <MobileMenuIcon
          className="cursor-pointer text-neutral-500 dark:text-neutral-500 hover:text-gray dark:hover:text-neutral-600 transition-colors"
          onClick={ toggleMobileMenu }
        />
      </div>
    );
  } else {
    const CollapseIcon = collapsed ? RiMenuUnfoldLine : RiMenuFoldLine;

    return (
      <div
        className="flex items-center justify-start min-w-8 min-h-6 pl-1 relative">
        <Transition show={ !collapsed }>
          <ScraperAPILogo className="h-6 w-min flex-shrink-0 transition-opacity duration-700 data-[closed]:opacity-0"/>
        </Transition>
        { showCollapseIcon &&
            <CollapseIcon
                className="absolute right-1 cursor-pointer text-neutral-500 dark:text-neutral-300 hover:text-gray dark:hover:text-neutral-400 duration-200 transition-colors"
                onClick={ toggleCollapsed }
            />
        }
      </div>
    );
  }
}

function ContactSupportSidebarAction({ collapsed }: { collapsed: boolean }) {
  const supportWidget = useSupportWidget();
  const user = useUser();

  return (
    <SidebarAction
      content="Contact Support"
      Icon={ Icons.Support }
      iconStyle="w-5 dark:w-4 h-5 dark:h-4"
      collapsed={ collapsed }
      onClick={ () => {
        supportWidget?.hideSalesForm?.();
        supportWidget?.showSupportForm(user?.email);
      } }
    />
  );
}

function ContactSalesSidebarAction({ collapsed }: { collapsed: boolean }) {
  const { contactSalesFn } = useContactSales();
  const supportWidget = useSupportWidget();

  return (
    <SidebarAction
      content="Contact Sales"
      Icon={ Icons.Sales }
      iconStyle="w-5 dark:w-4 h-5 dark:h-4"
      collapsed={ collapsed }
      onClick={ () => { supportWidget?.hideSupportForm?.(); contactSalesFn(); } }
    />
  );
}

// at the moment only divs are assigned to refs but more could be added
const checkRefContainsCurrent = (ref: MutableRefObject<HTMLDivElement | null>, e:MouseEvent) => ref.current && ref.current.contains(e.target as Node);

const WrapForMobile = ({ mobile, show, mobileMenuToggleCallback, refUserMenu, children }: { mobile: boolean, show: boolean, refUserMenu:any, mobileMenuToggleCallback: () => void, children: ReactNode }) => {
  const refContent = useRef<HTMLDivElement | null>(null);
  const refHeader = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mobile && show) {
      const handleClick = (e: MouseEvent) => {
        const inContent = checkRefContainsCurrent(refContent, e);
        const inHeader = checkRefContainsCurrent(refHeader, e);
        const inUserMenu = checkRefContainsCurrent(refUserMenu, e);

        if (!inHeader && !inContent && !inUserMenu) {
          mobileMenuToggleCallback();
        }
      };
      // Don't use 'click' here. inHeader will always be false. The ref won't contain the target.
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [ show, mobile, mobileMenuToggleCallback, refUserMenu ]);

  if (mobile) {
    return (
      <>
        <div ref={refHeader} className="select-none border-b border-borderColor dark:border-neutral-200 px-6 pt-3 pb-4 border-l-[3px] dark:border-l-0 border-l-transparent">
          <LogoAndIcons mobile toggleMobileMenu={mobileMenuToggleCallback}/>
        </div>

        <div ref={refContent}>
          <Transition show={ show } >
            <div className={ cx(
              "absolute top-0 left-0 h-full overflow-y-auto z-10 bg-white dark:bg-neutral-50",
              "transition-transform ease-in-out duration-500 data-[closed]:-translate-x-full"
            ) }
            >
              { children }
            </div>
          </Transition>
        </div>
      </>
    );
  } else {
    return <>{ children }</>;
  }
};

export default function Sidebar() {
  const [ collapsed, setCollapsed ] = useLocalStorage("saSidebarCollapsed", false);
  const isMobile = useIsMobile();
  const [ showMobileMenu, setShowMobileMenu ] = useState(false);
  const user = useUser();

  const reallyCollapsed = useMemo(() => collapsed && !isMobile, [ collapsed, isMobile ]);

  const refUserMenu = useRef<HTMLDivElement | null>(null);

  const isShortMenu = useMediaQuery({ query: "(max-height: 860px)" });

  return (
    <WrapForMobile mobile={ isMobile } show={showMobileMenu} mobileMenuToggleCallback={() => setShowMobileMenu(x => !x)} refUserMenu={refUserMenu}>
      <div className={
        cx(
          "dark",
          "relative group",
          "flex flex-col h-full",
          "text-gray dark:text-neutral-600 bg-white dark:bg-neutral-50 border-r border-borderColor dark:border-neutral-200",
          "overflow-y-auto overflow-x-hidden",
          "transition-all duration-300 ease-in-out",
          reallyCollapsed ? COLLAPSED_WIDTH : PANEL_WIDTH)
      }
      >
        <div className={ cx(
          "px-4 py-4 z-10 bg-white dark:bg-neutral-50",
          !isMobile && "sticky top-0"
          ) }
        >
          <LogoAndIcons
            showCollapseIcon={ !isMobile }
            toggleCollapsed={ () => setCollapsed(c => !c) }
            collapsed={ reallyCollapsed }
          />
        </div>

        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col">
            <div className="space-y-2 rebrand:space-y-0">
              <SidebarNavLink
                to={ HomePath }
                content={ user?.isFreeTrial ? "Get started" : "Dashboard" }
                Icon={ Icons.Dashboard }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}
              />

              <SidebarNavLink
                featureSwitch="REACT_APP_API_PLAYGROUND_USERS"
                to="/apiplayground"
                content="API playground"
                Icon={ Icons.APIPlayground }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                disableWhenBlocked
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}
              />

              <SidebarNavLink
                to="/projects"
                content="DataPipeline"
                Icon={ Icons.DataPipeline }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                disableWhenBlocked
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}
              />

              <SidebarNavLink
                to="/crawlers"
                content="Crawler"
                Icon={ Icons.Crawler }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                disableWhenBlocked
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}
                isNew={true}
                newUntil="2026-02-27"
                badgeText="Beta"
                matchSubPaths={true}
              />

              {reallyCollapsed ?
                <SidebarNavLink
                to="/analytics"
                content="Analytics"
                Icon={ Icons.Status }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                disableWhenBlocked
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}/>
              :
                <SidebarDisclosure
                collapsed={ reallyCollapsed }
              />
              }

              <SidebarNavLink
                to={ BillingPath }
                matchSubPaths
                content="Billing"
                Icon={ Icons.Billing }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}
              />

              <SidebarNavLink
                to="/mcp"
                content="MCP"
                Icon={ Icons.MCP }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}
                isNew={true}
                newUntil="2026-05-16"
              />

              <SidebarNavLink
                to="/more-apis"
                content="More APIs"
                Icon={ Icons.MoreAPIs }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                collapsed={ reallyCollapsed }
                onClick={() => {setShowMobileMenu(c => !c); }}
                isNew={true}
                newUntil="2027-01-01"
              />
            </div>
          </div>

          {/* Bottom section - stays at bottom */}
          <div className="flex flex-col">
            { !isShortMenu && !reallyCollapsed && (
              <div className="w-full">
                <UpgradeButton/>
              </div>
            ) }

            <div className="space-y-2 rebrand:space-y-0">
              <SidebarExtLink
                href="https://status.scraperapi.com"
                content={ <div className="flex flex-row gap-x-3 relative">
                  <div className="whitespace-nowrap">Status Page</div>
                  { !reallyCollapsed && <StatusBadge xOffset="left-[108px]" yOffset="top-[-10px]"/> }
                </div> }
                icon={ <>
                  <Icons.Status className="relative w-5 dark:w-4 h-5 dark:h-4"/>
                  { reallyCollapsed && <StatusBadge xOffset="right-[-14px]" yOffset="top-0"/> }
                </> }
                collapsed={ reallyCollapsed }
              />

              <SidebarExtLink
                href="https://docs.scraperapi.com/"
                content="Documentation"
                Icon={ Icons.Documentation }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                collapsed={ reallyCollapsed }
                trackedTitle="Documentation"
                iconName='Documentation'
              />

              <SidebarExtLink
                href="https://www.scraperapi.com/affiliates/"
                content="Referral program"
                Icon={ RiCoinsLine }
                iconStyle="w-5 dark:w-4 h-5 dark:h-4"
                collapsed={ reallyCollapsed }
                trackedTitle='Referral Program'
                iconName='Cash'
              />

              <ContactSupportSidebarAction collapsed={ reallyCollapsed }/>

              <ContactSalesSidebarAction collapsed={ reallyCollapsed }/>
            </div>

            <div className="pt-4">
              <div className="border-t border-borderColor dark:border-neutral-200"/>
            </div>

            <UserMenu collapsed={ reallyCollapsed } dropdownWidth={ USER_MENU_WIDTH } refUserMenu={refUserMenu}/>
          </div>
        </div>
      </div>
    </WrapForMobile>
  );
}
